
import { ConfiguratorType, OrderType } from "@biz/Definitions";
import { FacadeType } from "@biz/definitions/FacadeDefinitions";
import { ProductData3d } from "@biz/definitions/IVariantData3d";
import { ILoungeData } from "@biz/definitions/LoungeDefinitions";
import { IModelProductData } from "@biz/definitions/ModelProductDefinitions";
import { SST } from "@biz/definitions/SST";
import { ISunSailData } from "@biz/definitions/SunSailDefinitions";
import { ICurtain2dForm, ICurtainData } from "@biz/items/curtain/CurtainDefinitions";
import { INetData } from "@biz/items/net/NetDefinitions";
import { IRaffData } from "@biz/items/raff/RaffDefinitions";
import { IProduct2dForm, ISail2dForm, IStandardCollection2dForm } from "@biz/models/Sail2dDefinitions";
import { PD } from "@biz/PD";
import { IProductDescriptor } from "@biz/ProductDescriptor";
import { ProductData } from "@biz/projectCalculation/ProjectDefinitions";
import { SCProductData } from "@biz/sc/IShopData";
import { ISCSunSailData } from "@biz/sc/sunSail/ISCSunSailData";

export class Product {
  static isProduct2d(product: ProductData | undefined): product is IProduct2dForm {
    if (!product)
      return false;
    return "productType" in product;
  }

  static isProduct3d(product: ProductData | undefined): product is ProductData3d {
    if (!product)
      return false;
    return !Product.isProduct2d(product) && !Product.isProductSC(product);
  }

  static isProductSC(product: ProductData | undefined): product is SCProductData {
    if (!product)
      return false;
    if ("configuratorType" in product && product.configuratorType === "sc")
      return true;
    return false;
  }

  static isSunSail(product: ProductData | undefined): product is ISunSailData {
    if (!product)
      return false;
    return Product.isProduct3d(product) && (product as ISunSailData).itemType === "sunSail";
  }

  static isRaff(product: ProductData | undefined): product is IRaffData {
    if (!product)
      return false;
    return Product.isProduct3d(product) && (product as IRaffData).itemType === "raff";
  }

  static isCurtain(product: ProductData | undefined): product is ICurtainData {
    if (!product)
      return false;
    return Product.isProduct3d(product) && (product as ICurtainData).itemType === "curtain";
  }

  static isCurtain2d(product: ProductData | undefined): product is ICurtain2dForm {
    if (!product)
      return false;
    return Product.isProduct2d(product) && product.productType === "curtain2d";
  }
  static isStandardCollection2d(product: ProductData | undefined): product is IStandardCollection2dForm {
    if (!product)
      return false;
    return Product.isProduct2d(product) && product.productType === "standardCollection2d";
  }

  static isLounge(product: ProductData | undefined): product is ILoungeData {
    if (!product)
      return false;
    return Product.isProduct3d(product) && (product as ILoungeData).itemType === "lounge";
  }

  static isModelProduct(product: ProductData | undefined): product is IModelProductData {
    if (!product)
      return false;
    return Product.isProduct3d(product) && (product as IModelProductData).itemType === "modelProduct";
  }

  static isNet(product: ProductData | undefined): product is INetData {
    if (!product)
      return false;
    return Product.isProduct3d(product) && (product as INetData).itemType === "net";
  }

  static getOrderType(pd: ProductData): OrderType {
    return pd.orderType || "HS"; // if not set => return all products...
  }

  static calcSourceConfigurator(sunSail: ProductData): ConfiguratorType {
    if (Product.isProduct3d(sunSail))
      return sunSail.sourceConfigurator || "3d";
    if (Product.isProduct2d(sunSail))
      return "2d";
    if (Product.isProductSC(sunSail))
      return "sc";
    throw new Error("Source Configurator couldn't be calculated!");
    // return "sc";
  }

  static calcOrderType(pd: IProductDescriptor, configuratorType: ConfiguratorType): { initial: OrderType | undefined; possible: OrderType[]; } {
    if (configuratorType === "3d") {
      if (PD.isSail(pd)) {
        const sunSailType = pd.sunSailType;
        const poleCount = pd.poleCount;
        if (SST.is(sunSailType, SST.S))
          return { possible: ["HS"], initial: "HS" }
        else if (SST.is(sunSailType, SST.F))
          return { possible: ["H", "HS"], initial: "H" }
        else if (SST.is(sunSailType, SST.Flex))
          return { possible: ["H", "HS"], initial: "H" }
        else if (SST.is(sunSailType, SST.CsXsTwin) && [4, 6].includes(poleCount))
          return { possible: ["H", "HS"], initial: "H" }
        else if (SST.is(sunSailType, SST.Sandy))
          // return { possible: ["HS"], initial: "HS" }
          return { possible: ["H", "S", "HS"], initial: "HS" }
      } else if (PD.isRaff(pd)) {
        return { possible: ["HS"], initial: "HS" }
      } else if (PD.isCurtain(pd)) {
        return { possible: ["HS"], initial: "HS" }
      }
      return { possible: ["H"], initial: "H" }
    } else if (configuratorType === "2d") {
      if (PD.isSail(pd)) {
        const sunSailType = pd.sunSailType;
        if (SST.is(sunSailType, SST.Sandy))
          return { possible: ["H", "S", "HS"], initial: undefined }
      }
      return { possible: ["S"], initial: "S" };
    } else if (configuratorType === "sc") {
      return { possible: ["HS"], initial: "HS" };
    }
    return { possible: ["HS"], initial: "HS" };
  }

  static isAllInOne(pd: IProductDescriptor, configuratorType: ConfiguratorType): { required?: boolean; initial?: boolean; possible?: boolean; } {
    const ot = Product.calcOrderType(pd, configuratorType)
    return {
      required: ot.possible.length === 1 && ot.possible[0] === "HS",
      initial: ot.initial === "HS",
      possible: ot.possible.includes("HS")
    }
  }

  static getFacadeTypeFromConfigurator(configuratorType: ConfiguratorType): FacadeType {
    return configuratorType === "sc" ? "sc" : "app_new"
  }

  static getFacadeType(sunSail: ISunSailData | ISail2dForm | ISCSunSailData): FacadeType {
    return Product.getFacadeTypeFromConfigurator(Product.calcSourceConfigurator(sunSail));
  }
}